/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Settings › Governance → the governance copy (docs/CONTRACT-fleet-governance.md sections 8.5 and 12.4): the audit rows
 * Trends keeps past the audit log's window. It reads GET /v1/admin/governance/rows/status, which answers while the
 * governance-trends flag is off, and renders nothing on a 404 (an older service) or while the copy is empty and Trends is
 * off. While Trends is on it says what the copy holds; while Trends is off an IT admin may erase it whole after a
 * confirmation naming the count (POST /v1/admin/governance/rows/erase with that count). Auditors see the counts only.
 */
import { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { copyHoldsText, eraseAskText, trendsApi, trendsFailure, type GovRowsStatus } from '../lib/trends';
import { statusOf } from '../lib/fleetHttp';
import { Button, ConfirmDialog, Panel } from '../ui';
import { count } from '../ui/format';
import { SaveNote } from './SaveNote';

type Read = { state: 'loading' } | { state: 'absent' } | { state: 'failed'; text: string } | { state: 'ready'; status: GovRowsStatus };

export function GovernanceCopyCard() {
  const [read, setRead] = useState<Read>({ state: 'loading' });
  const [tick, setTick] = useState(0);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    let live = true;
    trendsApi.rowsStatus().then(
      (status) => { if (live) setRead({ state: 'ready', status }); },
      (error: unknown) => {
        if (!live) return;
        setRead(statusOf(error) === 404 ? { state: 'absent' } : { state: 'failed', text: trendsFailure(error, 'The governance copy could not be read.') });
      },
    );
    return () => {
      live = false;
    };
  }, [tick]);

  if (read.state === 'loading' || read.state === 'absent') return null;
  if (read.state === 'ready' && read.status.rows === 0 && !read.status.trendsOn && !note) return null;

  const erase = async (rows: number) => {
    setBusy(true);
    setAskError(null);
    try {
      const result = await trendsApi.eraseRows(rows);
      setAsking(false);
      setNote({ ok: true, text: `Erased ${count(result.erased)} rows.` });
      setTick((n) => n + 1);
    } catch (error) {
      const body = (error as { body?: { error?: unknown; rows?: unknown } }).body;
      if (statusOf(error) === 409 && body?.error === 'rows_changed') {
        setAsking(false);
        setNote({ ok: false, text: `The governance copy changed since its count was read${typeof body.rows === 'number' ? `: it now holds ${count(body.rows)} rows` : ''}. Nothing was erased; check the count and erase again.` });
        setTick((n) => n + 1);
      } else {
        setAskError(trendsFailure(error, 'Nothing was erased.'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="The governance copy" description="The audit rows Trends keeps so Show audit rows reaches past the audit log's window">
      <div className="ui-form">
        {read.state === 'failed' ? <SaveNote note={{ ok: false, text: read.text }} /> : null}
        {read.state === 'ready' && read.status.trendsOn ? (
          <p className="ui-para">{`${copyHoldsText(read.status)} Turn Trends off to erase it.`}</p>
        ) : null}
        {read.state === 'ready' && !read.status.trendsOn && read.status.rows > 0 ? (
          <>
            <p className="ui-para">{copyHoldsText(read.status)}</p>
            {read.status.canErase ? (
              <div className="ui-actions">
                <Button variant="danger" icon={Trash2} onClick={() => { setAskError(null); setAsking(true); }}>Erase the governance copy</Button>
              </div>
            ) : null}
          </>
        ) : null}
        <SaveNote note={note} />
      </div>
      {read.state === 'ready' ? (
        <ConfirmDialog
          open={asking}
          title="Erase the governance copy?"
          confirmLabel="Erase the governance copy"
          busy={busy}
          error={askError}
          onConfirm={() => void erase(read.status.rows)}
          onCancel={() => setAsking(false)}
        >
          {eraseAskText(read.status.rows)}
        </ConfirmDialog>
      ) : null}
    </Panel>
  );
}
